import { z } from 'zod';
import { LineItemListingSchema, PostOrderFulfillNestedProductSchema } from '../fulfillment/post-order-fulfill.ts';
import { PROJECT_CODE_SCHEMA } from '../project/project.ts';
import { PRODUCT_CATEGORY_SCHEMA } from '../order-product/product-category.ts';
import { CustomisedQuotePayloadByProductTypeSchema } from '../quote/quote-payload.ts';
import { CUSTOM_REQUEST_PRODUCT_TYPES_SCHEMA } from '../custom-request/mappings/custom-request-types.ts';

export const PostPositionFulfillmentUrlSchema = z.object({
    positionId: z.string().uuid(),
});

export const PostPositionFulfillmentResponseSchema = z.object({
    orderId: z.string().uuid(),
});

const BasePositionFulfillmentProductSchema = PostOrderFulfillNestedProductSchema.omit({
    quoteId: true,
    id: true,
}).extend({
    index: z.number().nonnegative(),
    isNewProduct: z.boolean(),
    showZeroSalesPrice: z.boolean(),
    showZeroPurchasePrice: z.boolean(),
    productType: CUSTOM_REQUEST_PRODUCT_TYPES_SCHEMA,
    quotePayload: CustomisedQuotePayloadByProductTypeSchema,
});

// Product WITH productId → new product so it needs type and category
const ProductWithProductIdSchema = BasePositionFulfillmentProductSchema.merge(
    z.object({
        isNewProduct: z.literal(true),
        productId: z.string().uuid(),
        category: PRODUCT_CATEGORY_SCHEMA,
    })
);

// Product WITHOUT productId → existing product so it needs positionProductId
const ProductWithPositionProductIdSchema = BasePositionFulfillmentProductSchema.merge(
    z.object({
        isNewProduct: z.literal(false),
        positionProductId: z.string(),
    })
);

const PositionFulfillmentProductSchema = z.discriminatedUnion('isNewProduct', [
    ProductWithProductIdSchema,
    ProductWithPositionProductIdSchema,
]);

const LineItemBase = LineItemListingSchema.omit({ productId: true });

const LineItemWithProductId = LineItemBase.extend({
    productId: z.string().uuid(),
});

const LineItemWithPositionProductId = LineItemBase.extend({
    positionProductId: z.string(),
});

const LineItemSchema = z.union([LineItemWithProductId, LineItemWithPositionProductId]);

export const PostPositionFulfillmentRequestSchema = z
    .object({
        clientInformation: z.object({
            projectCode: PROJECT_CODE_SCHEMA,
            userId: z.number().int(),
            manualCostCenter: z.string().trim().nonempty().optional(),
        }),
        products: z.array(PositionFulfillmentProductSchema),
        lineItemListing: z.array(LineItemSchema),
        fulfilledWithIndividualItemListing: z.boolean(),
        supportingDocuments: z
            .array(
                z.object({
                    description: z.string(),
                    urlPath: z.string(),
                    fileName: z.string(),
                })
            )
            .nonempty(),
    })
    .refine(
        data => {
            const lineItems = data.lineItemListing;
            const isGroupKeySet = lineItems.some(item => item.groupKey !== undefined);
            if (isGroupKeySet) {
                return lineItems.every(item => item.groupKey !== undefined);
            }
            return true;
        },
        {
            path: ['lineItemListing'],
            message: 'If groupKey is set for one line item, it must be set for all line items.',
        }
    );
